import { __decorate } from "tslib";
import { AutoEncoder, field, MapDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { AccessRightHelper } from './AccessRight.js';
import { LoadedPermissions } from './LoadedPermissions.js';
import { PermissionLevel } from '@stamhoofd/types/PermissionLevel';
import { Permissions } from './Permissions.js';
import { PermissionsResourceType } from '@stamhoofd/types/PermissionsResourceType';
import { PermissionsResourceKey } from './PermissionsResourceType.js';
export class UserPermissions extends AutoEncoder {
    globalPermissions = null;
    organizationPermissions = new Map();
    // Current list of groups
    /**
     * The global permissions of the user without the platform responsibilities that don't apply to the email
     * address of the user (PlatformConfig.responsibilityEmailDomains). Null when nothing is left.
     *
     * @param email Email address of the user these permissions belong to
     */
    applicableGlobalPermissions(platform, email) {
        const globalPermissions = this.globalPermissions;
        if (!globalPermissions) {
            return null;
        }
        if (globalPermissions.responsibilities.length === 0 || platform.config.canInheritPlatformResponsibilityPermissions(email)) {
            return globalPermissions;
        }
        const applicable = globalPermissions.clone();
        applicable.responsibilities = [];
        return applicable.isEmpty ? null : applicable;
    }
    /**
     * @param email Email address of the user these permissions belong to, see applicableGlobalPermissions
     */
    forPlatform(platform, email) {
        const globalPermissions = this.applicableGlobalPermissions(platform, email);
        if (!globalPermissions) {
            return null;
        }
        const platformRoles = platform.getRoles();
        const base = LoadedPermissions.from(globalPermissions, platformRoles, [], platform.config.responsibilities);
        if (base.hasFullAccess()) {
            // Since the prohibited organization level access rights are not automatically
            // inherited if you have full access, we'll need to add them manually for full platform admins
            base.add(LoadedPermissions.create({
                level: PermissionLevel.Full,
                accessRights: [...AccessRightHelper.prohibitedOrganizationLevelAccessRights()],
            }));
        }
        return base;
    }
    /**
     * @param email See forPlatform
     * @param options.inheritFromPlatform Whether the permissions this user was granted at the platform level, and that apply to this organization, are included in the result. Defaults to true. Set to false to only take the permissions that were granted for this organization itself into account.
     */
    forOrganization(organization, platform, email, options) {
        const base = LoadedPermissions.create({});
        if (options?.inheritFromPlatform ?? true) {
            const platformPermissions = this.forPlatform(platform, email);
            if (platformPermissions) {
                const tags = organization.meta.tags.length === 0 ? [PermissionsResourceKey.All] : organization.meta.tags;
                for (const tag of tags) {
                    const rp = platformPermissions.getMergedResourcePermissions(PermissionsResourceType.OrganizationTags, tag);
                    if (rp) {
                        base.add(rp);
                    }
                }
            }
        }
        const specific = this.forWithoutInherit(organization, platform);
        if (!specific) {
            if (base.isEmpty) {
                return null;
            }
            return base;
        }
        specific.add(base);
        return specific;
    }
    forWithoutInherit(organization, platform) {
        const permissions = this.organizationPermissions.get(organization.id) ?? null;
        if (!permissions) {
            return null;
        }
        const organizationRoles = organization?.privateMeta?.roles ?? [];
        const inheritedResponsibilityRoles = organization?.privateMeta?.inheritedResponsibilityRoles ?? [];
        const allResponsibilities = [...platform.config.responsibilities, ...(organization?.privateMeta?.responsibilities ?? [])];
        // Clone all external data to avoid mutating them because of the removeAccessRights call
        const result = LoadedPermissions.from(permissions.clone(), organizationRoles.map(r => r.clone()), inheritedResponsibilityRoles.map(r => r.clone()), allResponsibilities.map(r => r.clone()));
        // Some access rights are not allowed to be used directly in the organization permissions
        // they can only be passed from the platform permissions towards the organization permissions as access rights for tags (e.g. review event notifications)
        // So these access rights need to be filtered out here
        result.removeAccessRights(AccessRightHelper.prohibitedOrganizationLevelAccessRights());
        return result;
    }
    convertPlatformPatch(patch) {
        if (!this.globalPermissions) {
            const clonedPatch = UserPermissions.patch({
                globalPermissions: patch === null ? null : Permissions.create({}).patch(patch),
            });
            return clonedPatch;
        }
        return UserPermissions.patch({
            globalPermissions: patch,
        });
    }
    convertPatch(patch, organizationId) {
        if (!this.organizationPermissions.get(organizationId)) {
            const clonedPatch = UserPermissions.patch({});
            const p = patch === null ? null : Permissions.create({}).patch(patch);
            clonedPatch.organizationPermissions.set(organizationId, p);
            return clonedPatch;
        }
        const clonedPatch = UserPermissions.patch({});
        clonedPatch.organizationPermissions.set(organizationId, patch);
        return clonedPatch;
    }
    clearEmptyPermissions() {
        if (this.globalPermissions && this.globalPermissions.isEmpty) {
            this.globalPermissions = null;
        }
        for (const [organizationId, permissions] of this.organizationPermissions) {
            if (permissions.isEmpty) {
                this.organizationPermissions.delete(organizationId);
            }
        }
    }
    get isEmpty() {
        return this.globalPermissions === null && this.organizationPermissions.size === 0;
    }
    static limitedPatch(old, patch, organizationId) {
        if (patch === null) {
            return old;
        }
        if (patch.isPatch()) {
            // Only allow to set the permissions for the organization in scope
            if (patch.organizationPermissions.get(organizationId) !== undefined) {
                const clonedPatch = UserPermissions.patch({});
                clonedPatch.organizationPermissions.set(organizationId, patch.organizationPermissions.get(organizationId));
                const updated = old ? old.patch(clonedPatch) : UserPermissions.create({}).patch(clonedPatch);
                // Maintain responsibilities (not editable with a limited patch)
                const n = updated.organizationPermissions.get(organizationId);
                const o = old?.organizationPermissions.get(organizationId)?.responsibilities;
                if (n) {
                    n.responsibilities = o ?? [];
                }
                else {
                    if (o && o.length) {
                        // Keep responsibilities
                        const kept = Permissions.create({
                            responsibilities: o,
                        });
                        updated.organizationPermissions.set(organizationId, kept);
                    }
                }
                if (updated.isEmpty) {
                    return null;
                }
                return updated;
            }
        }
        else {
            // Only allow to set the permissions for the organization in scope
            if (patch.organizationPermissions.get(organizationId) !== undefined) {
                const clonedPatch = UserPermissions.patch({});
                clonedPatch.organizationPermissions.set(organizationId, patch.organizationPermissions.get(organizationId));
                const updated = old ? old.patch(clonedPatch) : UserPermissions.create({}).patch(clonedPatch);
                // Maintain responsibilities (not editable with a limited patch)
                const n = updated.organizationPermissions.get(organizationId);
                const o = old?.organizationPermissions.get(organizationId)?.responsibilities;
                if (n) {
                    n.responsibilities = o ?? [];
                }
                else {
                    if (o && o.length) {
                        // Keep responsibilities
                        const kept = Permissions.create({
                            responsibilities: o,
                        });
                        updated.organizationPermissions.set(organizationId, kept);
                    }
                }
                if (updated.isEmpty) {
                    return null;
                }
                return updated;
            }
        }
        return old;
    }
    static limitedAdd(old, add, organizationId) {
        if (add.organizationPermissions.get(organizationId)) {
            const cloned = old ? old.clone() : UserPermissions.create({});
            const realAdd = add.organizationPermissions.get(organizationId);
            const realOld = cloned ? cloned.organizationPermissions.get(organizationId) : null;
            if (!realOld) {
                cloned.organizationPermissions.set(organizationId, realAdd);
                return cloned;
            }
            realOld.add(realAdd);
            return cloned;
        }
        return old;
    }
    static add(old, add) {
        const cloned = old ? old.clone() : UserPermissions.create({});
        if (add.globalPermissions) {
            if (cloned.globalPermissions) {
                cloned.globalPermissions.add(add.globalPermissions);
            }
            else {
                cloned.globalPermissions = add.globalPermissions;
            }
        }
        for (const [organizationId, permissions] of add.organizationPermissions) {
            if (cloned.organizationPermissions.get(organizationId)) {
                cloned.organizationPermissions.get(organizationId).add(permissions);
            }
            else {
                cloned.organizationPermissions.set(organizationId, permissions);
            }
        }
        return cloned;
    }
}
__decorate([
    field({ decoder: Permissions, nullable: true })
], UserPermissions.prototype, "globalPermissions", void 0);
__decorate([
    field({ decoder: new MapDecoder(StringDecoder, Permissions) })
], UserPermissions.prototype, "organizationPermissions", void 0);
//# sourceMappingURL=UserPermissions.js.map